import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { SurveyCreatorComponent, SurveyCreator } from 'survey-creator-react'
import 'survey-creator-core/survey-creator-core.min.css'
import { adminApi } from '../services/adminApi'
import '../styles/builder.css'

export function SurveyBuilder() {
  const { surveyId: paramId } = useParams<{ surveyId?: string }>()
  const navigate = useNavigate()
  const creatorRef = useRef<SurveyCreator | null>(null)
  const [creator, setCreator] = useState<SurveyCreator | null>(null)
  const [surveyId, setSurveyId] = useState<string | null>(paramId || null)
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState<'draft' | 'published'>('draft')
  const [saving, setSaving] = useState(false)
  const [showNewModal, setShowNewModal] = useState(false)
  const [newId, setNewId] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [error, setError] = useState<string | null>(null)
  const publishAfterModal = useRef(false)

  useEffect(() => {
    const c = new SurveyCreator({
      showLogicTab: true,
      showTranslationTab: false,
      showJSONEditorTab: true,
      showThemeTab: false,
      isAutoSave: false,
    })

    // Disable file upload, signature, and html question types
    c.toolbox.removeItem('file')
    c.toolbox.removeItem('signaturepad')
    c.toolbox.removeItem('html')

    // No theme API — CSS handles dark styling

    creatorRef.current = c
    setCreator(c)

    if (paramId) {
      adminApi.getSurvey(paramId).then((survey) => {
        setTitle(survey.title || '')
        setStatus((survey.status as 'draft' | 'published') || 'draft')
        if (survey.definition && typeof survey.definition === 'object') {
          c.JSON = survey.definition
        }
      }).catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load survey')
      })
    }

    return () => {
      creatorRef.current = null
    }
  }, [paramId])

  const doSave = useCallback(async (publishNow: boolean) => {
    if (!creatorRef.current) return
    setSaving(true)
    setError(null)

    try {
      const definition = creatorRef.current.JSON
      const targetStatus = publishNow ? 'published' : status

      if (surveyId) {
        await adminApi.updateSurvey(surveyId, { title, definition, status: targetStatus })
        if (publishNow) setStatus('published')
      } else {
        // Need ID and title — show modal
        publishAfterModal.current = publishNow
        setShowNewModal(true)
        setSaving(false)
        return
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }, [surveyId, title, status])

  const handleModalSave = useCallback(async () => {
    if (!creatorRef.current || !newId.trim() || !newTitle.trim()) return
    setSaving(true)
    setError(null)
    setShowNewModal(false)

    try {
      const definition = creatorRef.current.JSON
      const targetStatus = publishAfterModal.current ? 'published' : 'draft'
      await adminApi.createSurvey({
        id: newId.trim(),
        title: newTitle.trim(),
        definition,
        status: targetStatus,
      })
      setSurveyId(newId.trim())
      setTitle(newTitle.trim())
      setStatus(targetStatus as 'draft' | 'published')
      navigate(`/admin/builder/${encodeURIComponent(newId.trim())}`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setSaving(false)
    }
  }, [newId, newTitle, navigate])

  return (
    <div className="builder-page">
      <div className="builder-header">
        <Link to="/admin/dashboard">← Dashboard</Link>
        <div className="builder-title">
          {surveyId ? (
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Survey title"
            />
          ) : (
            <span>New Survey</span>
          )}
        </div>
        <span className={`builder-status ${status}`}>{status}</span>
        <div className="builder-actions">
          <button onClick={() => doSave(false)} disabled={saving}>
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button className="publish-btn" onClick={() => doSave(true)} disabled={saving}>
            Publish
          </button>
        </div>
      </div>

      {error && <div style={{ padding: '0.5rem 1.25rem', color: '#fca5a5' }}>{error}</div>}

      <div className="builder-creator-wrap">
        {creator && <SurveyCreatorComponent creator={creator} />}
      </div>

      {showNewModal && (
        <div className="builder-modal-backdrop" onClick={() => setShowNewModal(false)}>
          <div className="builder-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Save New Survey</h2>
            <label>
              Survey ID
              <input value={newId} onChange={(e) => setNewId(e.target.value)} placeholder="e.g. customer-satisfaction-2026" />
            </label>
            <label>
              Title
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Customer Satisfaction Survey" />
            </label>
            <div className="builder-modal-actions">
              <button onClick={() => setShowNewModal(false)}>Cancel</button>
              <button onClick={handleModalSave} disabled={!newId.trim() || !newTitle.trim()}>
                {publishAfterModal.current ? 'Create & Publish' : 'Create Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
